<template>
  <!-- „Signatur bearbeiten" direkt über dem E-Mail-Fenster (Kundenfeedback
       Kanzlei Polz 24.10.2026, XII). Bisher führte der Knopf in die
       Organisationseinstellungen (Organigramm), von dort gab es keinen Weg
       zurück in die Akte. Jetzt bleibt man im Fenster, der Entwurf bleibt
       erhalten, und die geänderte Signatur steht sofort in der E-Mail. -->
  <v-dialog max-width="760" :model-value="modelValue" scrollable @update:model-value="close">
    <v-card class="sed" data-testid="signature-edit-dialog" rounded="lg">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ $gt('Signatur bearbeiten') }}</span>
        <ButtonBase color="grey" icon-style icon-type="cancel" @click="close" />
      </v-card-title>
      <v-card-text class="sed__body">
        <!-- Vorschau -->
        <section class="sed__section">
          <h3 class="sed__title">{{ $gt('Ihre Signatur') }}<span v-if="location?.name" class="sed__sub"> · {{ location.name }}</span></h3>
          <div v-if="previewLoading" class="sed__muted">{{ $gt('Vorschau wird geladen …') }}</div>
          <div v-else-if="!previewHtml" class="sed__muted">
            {{ $gt('Für Ihr Unternehmen ist noch keine Signatur-Vorlage hinterlegt.') }}
          </div>
          <!-- Aus der eigenen Vorlage gerendert (eigene Daten) – wie beim Versand. -->
          <div v-else class="sed__preview" data-testid="signature-edit-preview" v-html="previewHtml" />
        </section>

        <!-- Eigene Angaben -->
        <section class="sed__section">
          <h3 class="sed__title">{{ $gt('Ihre Angaben in der Signatur') }}</h3>
          <div class="sed__grid">
            <label class="sed__field">
              <span class="sed__label">{{ $gt('Telefon (Festnetz)') }}</span>
              <input v-model.trim="personal.landline" class="sed__input" data-testid="signature-edit-landline" type="tel">
            </label>
            <label class="sed__field">
              <span class="sed__label">{{ $gt('Mobil') }}</span>
              <input v-model.trim="personal.mobile" class="sed__input" data-testid="signature-edit-mobile" type="tel">
            </label>
            <!-- Kundenfeedback 26.10.2026 (Migration 0395): {{mobil}} erscheint nur bei
                 geschäftlicher Mobilnummer; gleiche Einstufung wie in den Stammdaten. -->
            <label class="sed__check" :title="$gt('Eine private Mobilnummer erscheint nicht in der Signatur und im Fall nur für Ihre eigene Organisation.')">
              <input v-model="personal.mobileIsBusiness" data-testid="signature-edit-mobile-business" type="checkbox">
              <span>{{ $gt('Mobilnummer ist geschäftlich (in der Signatur anzeigen)') }}</span>
            </label>
            <div class="sed__field">
              <span class="sed__label">
                {{ $gt('Stellenbezeichnung') }}
                <Info class="sed__info" :size="12" :title="$gt('Die Stellenbezeichnung pflegt die Verwaltung Ihres Unternehmens in der Mitarbeiterverwaltung. Ohne Eintrag steht dort Ihre Abteilung.')" />
              </span>
              <span class="sed__readonly">{{ jobTitle || '–' }}</span>
            </div>
            <label class="sed__check" :class="{ 'sed__check--off': !photo.info.hasProfileImage && !photo.info.hasPhoto }">
              <input
                :checked="photo.info.enabled"
                data-testid="signature-edit-photo"
                :disabled="photoSaving || (!photo.info.hasProfileImage && !photo.info.hasPhoto)"
                type="checkbox"
                @change="togglePhoto(($event.target as HTMLInputElement).checked)"
              >
              <span :title="!photo.info.hasProfileImage && !photo.info.hasPhoto ? $gt('Bitte laden Sie zuerst unter „Mein Profil“ ein Profilbild hoch.') : ''">
                {{ $gt('Foto in der Signatur anzeigen') }}
              </span>
            </label>
          </div>
          <div class="sed__actions">
            <button
              class="sed__btn sed__btn--primary"
              data-testid="signature-edit-save-personal"
              :disabled="!personalDirty || personalSaving"
              type="button"
              @click="savePersonal"
            >
              <v-progress-circular v-if="personalSaving" indeterminate size="14" width="2" />
              {{ $gt('Angaben speichern') }}
            </button>
          </div>
        </section>

        <!-- Vorlage des Unternehmens (nur Administratoren) -->
        <section class="sed__section">
          <h3 class="sed__title">{{ $gt('Aufbau der Signatur (Vorlage des Unternehmens)') }}</h3>
          <template v-if="canEditTemplate && location?.idCompany">
            <p class="sed__muted">{{ $gt('Die Vorlage gilt für alle Mitarbeitenden dieses Unternehmens. Ihre eigenen Angaben werden über die Platzhalter eingesetzt.') }}</p>
            <button
              v-if="!templateOpen"
              class="sed__btn"
              data-testid="signature-edit-template-open"
              type="button"
              @click="openTemplate"
            >
              <PenLine :size="14" />{{ $gt('Vorlage bearbeiten') }}
            </button>
            <div v-else class="sed__editor">
              <div v-if="templateLoading" class="sed__muted">{{ $gt('Vorlage wird geladen …') }}</div>
              <CompanySignatureEditor
                v-else
                :company="templateCompany"
                :main-location="templateLocation"
                :saved-signature="templateSignature"
                :saved-website="templateWebsite"
                :saving="templateSaving"
                @save="saveTemplate"
              />
            </div>
          </template>
          <p v-else class="sed__muted">
            {{ $gt('Aufbau, Logo und Pflichtangaben legt die Verwaltung Ihres Unternehmens fest. Nur für diese E-Mail ändern? Bearbeiten Sie die Signatur einfach direkt im Text der E-Mail.') }}
          </p>
        </section>
      </v-card-text>
      <v-divider />
      <v-card-actions class="d-flex justify-end ma-2">
        <ButtonBase button-text="Fertig" icon-type="check" variant="flat" @click="close" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
  import type { SignatureLocation } from '@/services/emailSignature'
  import { Info, PenLine } from 'lucide-vue-next'
  import { computed, reactive, ref, watch } from 'vue'
  import ButtonBase from '@/components/buttons/ButtonBase.vue'
  import { gt } from '@/i18n/gt'
  import CompanySignatureEditor from '@/pages/pageOrganization/companyArea/CompanySignatureEditor.vue'
  import { prepareProfileSettingsForLoad, prepareProfileSettingsForSave } from '@/pages/pageUserSettings/model'
  import { getRequestGeneric, putRequestGeneric } from '@/services/backendApiGeneric'
  import {
    invalidateCompanySignature,
    loadSignatureDefaults,
    renderUserSignature,
    resolveSignatureContext,
  } from '@/services/emailSignature'
  import { loadSignaturePhoto, saveSignaturePhotoEnabled, useSignaturePhoto } from '@/services/signaturePhoto'
  import { useAuthStore } from '@/stores/authStore'
  import { useSnackbarStore } from '@/stores/snackbarStore'

  const props = defineProps<{
    modelValue: boolean
    /** Standort, für den das E-Mail-Fenster gerade unterschreibt. */
    location: SignatureLocation | null
  }>()
  const emit = defineEmits<{ 'update:modelValue': [open: boolean], 'saved': [] }>()

  const auth = useAuthStore()
  const snackbar = useSnackbarStore()
  const photo = useSignaturePhoto()

  const previewHtml = ref('')
  const previewLoading = ref(false)

  const personal = reactive({ landline: '', mobile: '', mobileIsBusiness: false })
  const savedPersonal = reactive({ landline: '', mobile: '', mobileIsBusiness: false })
  const personalSaving = ref(false)
  const photoSaving = ref(false)
  const personalDirty = computed(() => personal.landline !== savedPersonal.landline || personal.mobile !== savedPersonal.mobile
    || personal.mobileIsBusiness !== savedPersonal.mobileIsBusiness)
  const jobTitle = computed(() => auth.signatureJobTitle)

  const canEditTemplate = computed(() => auth.isAdministrator || auth.isSuperAdmin)
  const templateOpen = ref(false)
  const templateLoading = ref(false)
  const templateSaving = ref(false)
  const templateSignature = ref('')
  const templateWebsite = ref('')
  const templateCompanyName = ref('')
  const templateCompany = computed(() => ({ id: props.location?.idCompany, name: templateCompanyName.value }))
  const templateLocation = computed(() => {
    const loc = props.location
    if (!loc) return null
    const address = loc.address || [loc.streetNo, [loc.zip, loc.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    return { ...loc, address }
  })

  async function renderPreview () {
    previewLoading.value = true
    try {
      const defaults = await loadSignatureDefaults(auth.idOrganization)
      const ctx = await resolveSignatureContext(props.location, defaults)
      previewHtml.value = renderUserSignature(ctx, defaults)
    } catch {
      previewHtml.value = ''
    } finally {
      previewLoading.value = false
    }
  }

  function syncPersonalFromStore () {
    personal.landline = String(auth.landline || '')
    personal.mobile = String(auth.mobile || '')
    personal.mobileIsBusiness = auth.mobileIsBusiness
    savedPersonal.landline = personal.landline
    savedPersonal.mobile = personal.mobile
    savedPersonal.mobileIsBusiness = personal.mobileIsBusiness
  }

  /** Telefonnummern im Profil speichern – derselbe Weg wie „Mein Profil". */
  async function savePersonal () {
    if (!personalDirty.value || personalSaving.value) return
    personalSaving.value = true
    try {
      const data = await getRequestGeneric(`/protected/users/load-settings/${auth.idUser}`)
      const model = { ...prepareProfileSettingsForLoad(data), landline: personal.landline, mobile: personal.mobile, mobileIsBusiness: personal.mobileIsBusiness }
      const payload = await prepareProfileSettingsForSave(model)
      payload.userRole = auth.userRole
      await putRequestGeneric(`/protected/users/save-settings/${auth.idUser}`, payload, true)
      await auth.fetchUser()
      syncPersonalFromStore()
      await renderPreview()
      emit('saved')
      snackbar.showSuccess(gt('Ihre Angaben wurden gespeichert.'))
    } catch (error) {
      snackbar.showError((error as Error)?.message || gt('Angaben konnten nicht gespeichert werden'))
    } finally {
      personalSaving.value = false
    }
  }

  async function togglePhoto (enabled: boolean) {
    photoSaving.value = true
    try {
      await saveSignaturePhotoEnabled(enabled)
      await renderPreview()
      emit('saved')
    } catch (error) {
      snackbar.showError((error as Error)?.message || gt('Einstellung konnte nicht gespeichert werden'))
    } finally {
      photoSaving.value = false
    }
  }

  async function openTemplate () {
    templateOpen.value = true
    const idCompany = props.location?.idCompany
    if (!idCompany) return
    templateLoading.value = true
    try {
      const comm = await getRequestGeneric(`/protected/companies/${idCompany}/communication`) as Record<string, unknown> | null
      templateSignature.value = String(comm?.emailSignature || '')
      templateWebsite.value = String(comm?.website || '')
      templateCompanyName.value = String(comm?.companyName || '')
    } catch {
      snackbar.showError(gt('Vorlage konnte nicht geladen werden'))
      templateOpen.value = false
    } finally {
      templateLoading.value = false
    }
  }

  async function saveTemplate ({ signatureHtml, website }: { signatureHtml: string, website: string }) {
    const idCompany = props.location?.idCompany
    if (!idCompany) return
    templateSaving.value = true
    try {
      await putRequestGeneric(`/protected/companies/${idCompany}/communication`, { emailSignature: signatureHtml, website }, true)
      templateSignature.value = signatureHtml
      templateWebsite.value = website
      invalidateCompanySignature(idCompany)
      await renderPreview()
      emit('saved')
      snackbar.showSuccess(gt('Signatur-Vorlage gespeichert.'))
    } catch (error) {
      snackbar.showError((error as Error)?.message || gt('Vorlage konnte nicht gespeichert werden'))
    } finally {
      templateSaving.value = false
    }
  }

  function close () {
    emit('update:modelValue', false)
  }

  watch(() => props.modelValue, async open => {
    if (!open) return
    templateOpen.value = false
    syncPersonalFromStore()
    await loadSignaturePhoto(true)
    await renderPreview()
  }, { immediate: true })
</script>

<style scoped>
.sed__body { display: flex; flex-direction: column; gap: 18px; padding: 4px 20px 16px !important; }
.sed__section { display: flex; flex-direction: column; gap: 10px; }
.sed__section + .sed__section { padding-top: 16px; border-top: 1px solid #eef0f3; }
.sed__title { margin: 0; font-size: 0.9rem; font-weight: 700; color: var(--dark-color); }
.sed__sub { font-weight: 400; color: var(--grey-color); }
.sed__muted { margin: 0; font-size: 0.8rem; color: var(--grey-color); }
.sed__preview {
  padding: 14px 16px; overflow-x: auto;
  border: 1px solid var(--border-grey-color); border-radius: 10px;
  background: #fff; font-size: 0.88rem; color: var(--dark-color);
}
.sed__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px 16px; align-items: end; }
.sed__field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.sed__label { display: inline-flex; align-items: center; gap: 4px; font-size: 0.74rem; font-weight: 600; color: var(--grey-color); }
.sed__info { cursor: help; }
.sed__input {
  height: 36px; padding: 0 10px;
  border: 1px solid var(--border-grey-color); border-radius: 8px; outline: none;
  background: #fff; font-size: 0.86rem; color: var(--dark-color);
}
.sed__input:focus { border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(0, 118, 229, 0.14); }
.sed__readonly { min-height: 36px; display: flex; align-items: center; font-size: 0.86rem; color: var(--dark-color); }
.sed__check { display: flex; align-items: center; gap: 8px; min-height: 36px; font-size: 0.84rem; color: var(--dark-color); cursor: pointer; }
.sed__check input { accent-color: var(--primary-color); }
.sed__check--off { opacity: 0.6; cursor: default; }
.sed__actions { display: flex; justify-content: flex-end; }
.sed__btn {
  display: inline-flex; align-items: center; gap: 6px; align-self: flex-start;
  padding: 7px 14px; border: 1px solid var(--border-grey-color); border-radius: 8px;
  background: #fff; color: var(--dark-color); font-size: 0.8rem; font-weight: 600; cursor: pointer;
}
.sed__btn:hover:not(:disabled) { border-color: var(--primary-color); color: var(--primary-color); }
.sed__btn--primary { border-color: var(--primary-color); background: var(--primary-color); color: #fff; }
.sed__btn--primary:hover:not(:disabled) { color: #fff; background: #0066c7; }
.sed__btn:disabled { opacity: 0.5; cursor: default; }
.sed__editor { margin-top: 4px; }
</style>
